/**
 * Units utility functions
 * Provides unit symbols and conversions based on the current language
 */

import i18n from "./i18n";

/**
 * Get the weight unit based on current language
 * @returns Weight unit (lbs for English, kg for others)
 */
export function getWeightUnit(): string {
  const language = i18n.language || "fr";

  // English uses pounds (lbs)
  if (language.startsWith("en")) {
    return "lbs";
  }

  // French, Arabic, and others use kilograms (kg)
  return "kg";
}

/**
 * Convert weight from kg to the appropriate unit based on language
 * @param weightInKg - Weight in kilograms
 * @returns Weight in the appropriate unit for current language
 */
export function convertWeight(weightInKg: number): number {
  const language = i18n.language || "fr";

  // Convert to pounds for English (1 kg = 2.20462 lbs)
  if (language.startsWith("en")) {
    return weightInKg * 2.20462;
  }

  // Return as-is for other languages (kg)
  return weightInKg;
}

/**
 * Format weight with appropriate unit based on current language
 * @param weightInKg - Weight in kilograms (stored value)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted weight string with unit
 */
export function formatWeight(weightInKg: number, decimals: number = 1): string {
  const converted = convertWeight(weightInKg);
  const unit = getWeightUnit();
  return `${converted.toFixed(decimals)} ${unit}`;
}

/**
 * Get weight label for charts/tables (e.g., "Quantity (kg)" or "Quantity (lbs)")
 * @param label - The base label (e.g., "Quantity")
 * @returns Label with weight unit
 */
export function getWeightLabel(label: string): string {
  const unit = getWeightUnit();
  return `${label} (${unit})`;
}
